import React, { useState, useEffect } from "react";
import { Tabs, Tab, useMediaQuery, Theme } from "@mui/material";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import CategoriesList from "@/pages/settings/component/CategoriesList";
import ProfilePage from "../profile";
import CompanyPage from "../company";
import ComingSoonPage from "./component/ComingSoonPage";
import ShippingPage from "./component/shipping-page";
import { useSearchParams } from "react-router-dom";

function SettingsPage() {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const isTablet = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Get tab from URL parameter
  const getTabFromUrl = () => {
    const tabParam = searchParams.get("tab");
    switch (tabParam) {
      case "profile":
        return 0;
      case "company":
        return 1;
      case "settings":
        return 2;
      case "payment":
        return 3;
      case "shipping":
        return 4;
      case "permissions":
        return 5;
      case "categories":
        return 6;
      default:
        return 0; // Default to profile tab
    }
  };

  const [activeTab, setActiveTab] = useState(getTabFromUrl());

  // Update URL when tab changes
  const updateUrlForTab = (tabIndex: number) => {
    let tabParam = "";
    switch (tabIndex) {
      case 0:
        tabParam = "profile";
        break;
      case 1:
        tabParam = "company";
        break;
      case 2:
        tabParam = "settings";
        break;
      case 3:
        tabParam = "payment";
        break;
      case 4:
        tabParam = "shipping";
        break;
      case 5:
        tabParam = "permissions";
        break;
      case 6:
        tabParam = "categories";
        break;
      default:
        tabParam = "profile";
    }

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", tabParam);
    setSearchParams(newSearchParams);
  };

  // Sync active tab with URL changes
  useEffect(() => {
    setActiveTab(getTabFromUrl());
  }, [searchParams]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    updateUrlForTab(newValue);
  };

  useEffect(() => {
    if (!searchParams.get("tab")) {
      updateUrlForTab(0);
    }
  }, []);

  const tabLabels = [
    t("navigation.MyProfile"),
    t("checkout.fields.company"),
    t("navigation.Settings"),
    t("status.payment"),
    t("status.shipping"),
    t("general.permissions"),
    t("company.fields.categories"),
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return <ProfilePage />;
      case 1:
        return <CompanyPage />;
      case 2:
        return <ComingSoonPage />;
      case 3:
        return <ComingSoonPage />;
      case 4:
        return <ShippingPage />;
      case 5:
        return <ComingSoonPage />;
      case 6:
        return <CategoriesList />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t("navigation.Settings")}
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs and Content */}
      <div className="max-w-7xl mx-auto md:px-4 px-2 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {/* Modern Tabs */}
          <div className="border-b border-gray-200 bg-gray-50">
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              textColor="primary"
              indicatorColor="primary"
              variant={
                isMobile ? "scrollable" : isTablet ? "scrollable" : "standard"
              }
              scrollButtons={isMobile ? "auto" : isTablet ? "auto" : undefined}
              sx={{
                "& .MuiTabs-scroller": {
                  overflow: "scroll !important",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
                "& .MuiTabs-flexContainer": {
                  justifyContent: "flex-start",
                  gap: "4px",
                  padding: "0 16px",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#18181b",
                  height: "3px",
                  borderRadius: "2px 2px 0 0",
                },
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  minHeight: "48px",
                  padding: "12px 16px",
                  color: "#6b7280",
                  borderBottom: "2px solid transparent",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "rgba(59, 130, 246, 0.04)",
                  },
                  "&.Mui-selected": {
                    color: "#18181b",
                    fontWeight: 600,
                  },
                },
              }}
            >
              {tabLabels.map((label, index) => (
                <Tab
                  key={index}
                  label={label}
                  sx={{
                    borderRadius: "8px 8px 0 0",
                    marginRight: "4px",
                    "&.Mui-selected": {
                      backgroundColor: "white",
                      borderBottom: "2px solid #644980",
                    },
                  }}
                />
              ))}
            </Tabs>
          </div>

          {/* Content Area */}
          <div className="md:p-6 bg-white">{renderTabContent()}</div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPage;
